import { Link } from '@tanstack/react-router';
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { IconCash, IconDrivers, IconRides, IconStar } from '../components/icons';
import type { MapFilter, Picked } from '../components/LiveMap';
import { ConnectionStatus } from '../components/Shell';
import { Avatar, Chips, Empty, Loading, PageHead, Pill, RideStatusPill, Stat, VehicleTile } from '../components/ui';
import { ago, count, longDay, phone, RIDE_STATUS_LABEL, rupees, time, VEHICLE_LABEL } from '../lib/format';
import { useActiveSos, useDashboard, useLiveRides, useOnlineDrivers } from '../lib/queries';
import type { AdminRide, OnlineDriver } from '../lib/types';

const LiveMap = lazy(() => import('../components/LiveMap'));

/** Ticks every 15 s so "waiting 4 min" style text stays true without a refetch. */
function useNow(ms = 15_000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

const mins = (fromIso: string | null | undefined, now: number) => (fromIso ? Math.floor((now - new Date(fromIso).getTime()) / 60_000) : 0);

/** Why an open ride needs a person to look at it (empty = fine). */
export function attentionReasons(r: AdminRide, now: number): string[] {
  const out: string[] = [];
  const waiting = r.status === 'REQUESTED' || r.status === 'OFFERING';
  if (waiting) {
    const left = r.matchDeadline ? (new Date(r.matchDeadline).getTime() - now) / 1000 : Infinity;
    if (left < 45) out.push('About to expire — no driver yet');
    else if (now - new Date(r.requestedAt).getTime() > 60_000) out.push(`Waiting for a driver for ${Math.max(1, mins(r.requestedAt, now))} min`);
  }
  if (r.pinFailures >= 2) out.push(`Wrong PIN entered ${r.pinFailures} times`);
  if (['ACCEPTED', 'ARRIVED', 'STARTED'].includes(r.status)) {
    const at = r.driver?.location?.at;
    if (!at || now - new Date(at).getTime() > 120_000) out.push(`Driver’s phone silent since ${at ? time(at) : 'acceptance'}`);
  }
  if (r.status === 'ARRIVED' && mins(r.arrivedAt, now) >= 10) out.push(`Driver waiting at pickup for ${mins(r.arrivedAt, now)} min`);
  if (r.status === 'STARTED' && r.startedAt) {
    const expected = Math.max(r.durationSeconds * 2, r.durationSeconds + 20 * 60) * 1000;
    if (now - new Date(r.startedAt).getTime() > expected) out.push(`Trip running long — started ${time(r.startedAt)}`);
  }
  return out;
}

function PickCard({ picked, drivers, rides, onClose }: { picked: Picked; drivers: OnlineDriver[]; rides: AdminRide[]; onClose: () => void }) {
  if (!picked || picked.kind === 'sos') return null;
  if (picked.kind === 'driver') {
    const d = drivers.find((x) => x.id === picked.id);
    if (!d) return null;
    return (
      <div className="map-pick" data-testid="map-pick">
        <div className="person">
          {d.vehicleType ? <VehicleTile type={d.vehicleType} /> : <Avatar name={d.name} />}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="strong truncate">{d.name ?? 'Unnamed driver'}</div>
            <div className="small muted truncate">
              {d.vehicleType ? VEHICLE_LABEL[d.vehicleType] : 'No vehicle'}
              {d.plate ? ` · ${d.plate}` : ''} · seen {ago(d.location?.at)}
            </div>
          </div>
          {d.activeRide ? <Pill tone="accent">{RIDE_STATUS_LABEL[d.activeRide.status]}</Pill> : <Pill tone="good">Available</Pill>}
        </div>
        <div className="actions">
          <button className="btn btn-sm" onClick={onClose}>
            Close
          </button>
          {d.activeRide && (
            <Link to="/rides/$rideId" params={{ rideId: d.activeRide.id }} className="btn btn-sm">
              Open ride
            </Link>
          )}
          <Link to="/drivers/$driverId" params={{ driverId: d.id }} className="btn btn-sm btn-dark">
            Open driver
          </Link>
        </div>
      </div>
    );
  }
  const r = rides.find((x) => x.id === picked.id);
  if (!r) return null;
  return (
    <div className="map-pick" data-testid="map-pick">
      <div className="spread" style={{ flexWrap: 'nowrap' }}>
        <div style={{ minWidth: 0 }}>
          <div className="strong truncate">{r.rider?.name ?? phone(r.rider?.phone)}</div>
          <div className="small muted truncate">
            {VEHICLE_LABEL[r.vehicleType]} · {rupees(r.farePaise)} · booked {time(r.requestedAt)}
          </div>
        </div>
        <RideStatusPill status={r.status} />
      </div>
      <div className="small muted truncate" style={{ marginTop: 8 }}>
        {r.pickup.address ?? 'Pickup'} → {r.drop.address ?? 'Drop'}
      </div>
      <div className="actions">
        <button className="btn btn-sm" onClick={onClose}>
          Close
        </button>
        <Link to="/rides/$rideId" params={{ rideId: r.id }} className="btn btn-sm btn-dark">
          Open ride
        </Link>
      </div>
    </div>
  );
}

export function LivePage() {
  const now = useNow();
  const dash = useDashboard();
  const rides = useLiveRides();
  const drivers = useOnlineDrivers();
  const sos = useActiveSos();
  const [filter, setFilter] = useState<MapFilter>('all');
  const [picked, setPicked] = useState<Picked>(null);
  const [focusKey, setFocusKey] = useState(0);

  const liveRides = useMemo(() => rides.data ?? [], [rides.data]);
  const attention = useMemo(
    () => liveRides.map((r) => ({ ride: r, why: attentionReasons(r, now) })).filter((x) => x.why.length),
    [liveRides, now],
  );
  const onlineDrivers = useMemo(() => [...(drivers.data ?? [])].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')), [drivers.data]);
  const busy = onlineDrivers.filter((d) => d.activeRide).length;
  const waiting = liveRides.filter((r) => !r.driver).length;
  const openRides = Object.values(dash.data?.rides.open ?? {}).reduce((a, b) => a + (b ?? 0), 0);
  const pendingDrivers = dash.data?.drivers.byStatus.PENDING ?? 0;

  useEffect(() => {
    if (!picked) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setPicked(null);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [picked]);

  const pickFromList = (p: Picked) => {
    setPicked(p);
    setFocusKey((k) => k + 1);
  };

  return (
    <div className="stack" style={{ gap: 18 }}>
      <PageHead
        title="Live"
        sub={
          <span className="row" style={{ gap: 14 }}>
            <span>
              {longDay(new Date(now))} · {time(new Date(now))}
            </span>
            <ConnectionStatus />
          </span>
        }
        actions={
          pendingDrivers > 0 ? (
            <Link to="/drivers" search={{ status: 'PENDING' }} className="btn">
              {pendingDrivers} driver{pendingDrivers === 1 ? '' : 's'} to review
            </Link>
          ) : undefined
        }
      />

      <div className="stats compact">
        <Stat
          icon={IconDrivers}
          label="Drivers online"
          testId="stat-online"
          value={drivers.data ? count(onlineDrivers.length) : '—'}
          foot={busy ? `${busy} on a ride` : 'All available'}
        />
        <Stat
          icon={IconRides}
          label="Open rides"
          value={dash.data ? count(openRides) : '—'}
          foot={attention.length ? `${attention.length} need attention` : waiting ? `${waiting} looking for a driver` : 'Nothing waiting'}
          attn={attention.length > 0}
        />
        <Stat icon={IconStar} label="Completed today" value={dash.data ? count(dash.data.rides.completedToday) : '—'} foot="India time" />
        <Stat icon={IconCash} label="Fares today" value={dash.data ? rupees(dash.data.rides.faresTodayPaise) : '—'} foot="Agreed fares, completed" />
      </div>

      {attention.length > 0 && (
        <section className="card" aria-label="Needs attention">
          <div className="card-head">
            <h2>
              Needs attention <span className="count-chip">{attention.length}</span>
            </h2>
            <span className="muted small">Oldest first</span>
          </div>
          <div className="list">
            {attention.map(({ ride, why }) => (
              <Link key={ride.id} to="/rides/$rideId" params={{ rideId: ride.id }} className="list-item attention">
                <div className="grow">
                  <div className="truncate strong">{why[0]}</div>
                  <div className="small muted truncate">
                    {VEHICLE_LABEL[ride.vehicleType]} · {ride.rider?.name ?? phone(ride.rider?.phone)} · {ride.pickup.address ?? 'Pickup on map'}
                    {why.length > 1 ? ` · +${why.length - 1} more` : ''}
                  </div>
                </div>
                <RideStatusPill status={ride.status} />
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="live-grid">
        <section className="card map-card" aria-label="Map">
          <Suspense fallback={<Loading rows={3} />}>
            <LiveMap
              drivers={onlineDrivers}
              rides={liveRides}
              sos={sos.data ?? []}
              filter={filter}
              picked={picked}
              focusKey={focusKey}
              ready={!drivers.isLoading && !rides.isLoading && !sos.isLoading}
              onPick={setPicked}
            />
          </Suspense>
          <div className="map-chips">
            <Chips<MapFilter>
              label="Show on the map"
              value={filter}
              onChange={setFilter}
              options={[
                { value: 'all', label: 'All' },
                { value: 'free', label: 'Available', count: onlineDrivers.length - busy },
                { value: 'busy', label: 'On a ride', count: busy },
                { value: 'waiting', label: 'Waiting', count: waiting },
              ]}
            />
          </div>
          {!picked && (
            <div className="map-legend" aria-hidden="true">
              <span className="legend-item">
                <span className="legend-swatch" style={{ background: '#0D0D0D', boxShadow: '0 0 0 1.5px #fff' }} /> Available
              </span>
              <span className="legend-item">
                <span className="legend-swatch" style={{ background: '#5CE1E6' }} /> On a ride
              </span>
              <span className="legend-item">
                <span className="legend-swatch" style={{ background: '#E3A21A' }} /> Waiting
              </span>
              {!!sos.data?.length && (
                <span className="legend-item">
                  <span className="legend-swatch" style={{ background: '#D92D20' }} /> SOS
                </span>
              )}
            </div>
          )}
          <PickCard picked={picked} drivers={onlineDrivers} rides={liveRides} onClose={() => setPicked(null)} />
        </section>

        <div className="live-side">
          <section className="card" aria-label="Live rides">
            <div className="card-head">
              <h2>
                Live rides <span className="count-chip">{liveRides.length}</span>
              </h2>
              <Link to="/rides" className="link small">
                All rides
              </Link>
            </div>
            {rides.isLoading ? (
              <Loading rows={3} />
            ) : liveRides.length === 0 ? (
              <Empty title="No rides right now" icon={IconRides}>
                New bookings appear here as they happen.
              </Empty>
            ) : (
              <div className="list" data-testid="live-rides">
                {liveRides.map((r) => (
                  <Link
                    key={r.id}
                    to="/rides/$rideId"
                    params={{ rideId: r.id }}
                    className={`list-item${picked?.kind === 'ride' && picked.id === r.id ? ' selected' : ''}`}
                    onMouseEnter={() => setPicked({ kind: 'ride', id: r.id })}
                    onFocus={() => pickFromList({ kind: 'ride', id: r.id })}
                  >
                    <VehicleTile type={r.vehicleType} small />
                    <div className="grow">
                      <div className="truncate strong">
                        {r.rider?.name ?? phone(r.rider?.phone)}
                        <span className="faint" style={{ fontWeight: 500 }}>
                          {' '}
                          · {rupees(r.farePaise)}
                        </span>
                      </div>
                      <div className="small muted truncate">
                        {time(r.requestedAt)} · {r.pickup.address ?? 'Pickup'} → {r.drop.address ?? 'Drop'}
                      </div>
                    </div>
                    <RideStatusPill status={r.status} />
                  </Link>
                ))}
              </div>
            )}
          </section>

          <section className="card" aria-label="Drivers online">
            <div className="card-head">
              <h2>
                Drivers online <span className="count-chip">{onlineDrivers.length}</span>
              </h2>
              <Link to="/drivers" search={{ online: true }} className="link small">
                All drivers
              </Link>
            </div>
            {drivers.isLoading ? (
              <Loading rows={3} />
            ) : onlineDrivers.length === 0 ? (
              <Empty title="No drivers online" icon={IconDrivers}>
                Drivers appear here when they go online in the driver app.
              </Empty>
            ) : (
              <div className="list" data-testid="online-drivers">
                {onlineDrivers.map((d) => (
                  <div key={d.id} className={`list-item${picked?.kind === 'driver' && picked.id === d.id ? ' selected' : ''}`} style={{ padding: 0 }}>
                    <Link
                      to="/drivers/$driverId"
                      params={{ driverId: d.id }}
                      className="row-link"
                      style={{ minHeight: 58, padding: '10px 6px 10px 18px' }}
                      data-testid={`online-driver-${d.id}`}
                    >
                      {d.vehicleType ? <VehicleTile type={d.vehicleType} small /> : <Avatar name={d.name} />}
                      <span className="row-main">
                        <span className="row-title truncate">{d.name ?? 'Unnamed driver'}</span>
                        <span className="row-sub truncate">
                          {d.plate ? `${d.plate} · ` : ''}
                          <span data-testid="seen">Seen {ago(d.location?.at, now)}</span>
                        </span>
                      </span>
                      {d.activeRide ? <Pill tone="accent">On a ride</Pill> : <Pill>Available</Pill>}
                    </Link>
                    <button
                      type="button"
                      className="btn btn-ghost icon-btn btn-sm"
                      style={{ marginRight: 10 }}
                      aria-label={`Show ${d.name ?? 'driver'} on the map`}
                      disabled={!d.location}
                      onClick={() => pickFromList({ kind: 'driver', id: d.id })}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="3" />
                        <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
                        <circle cx="12" cy="12" r="7.5" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
